/**
 * CONTRACT FILE. Destination: src/core/engine/types.ts
 * Implemented by:
 *   - W1-05a (engine-eval):  pure evaluation over IR (anim, channels, floor integrators, line state, composition,
 *                            text, controls, maxTime, bezier registry, viewport) as functions;
 *   - W1-05b (engine-graph): src/core/engine/engine.ts exports `createEngine: CreateEngine` (dirty DAG, lazy
 *                            lowering cache, queries, FrameDrawList pooling; calls the seams below in R§8 order);
 *   - W1-06 (engine-notes-frame): src/core/engine/notes/index.ts exports `notesModule: EngineNotesModule`;
 *   - W1-08 (formats-legacy-respack): src/core/engine/effects/index.ts exports `effectsModule: EngineEffectsModule`
 *                            (extra.json effect and video evaluation share the extra.json keyframe rules).
 * Neither seam package edits another's files; engine.ts only delegates.
 *
 * The evaluation core (DESIGN.md D5): pure TypeScript, no DOM; implements Phira's semantics ON THE IR
 * (core/ir) with Phira's f64 times and f32 values (Math.fround). Runs on the main thread (preview,
 * timeline curves, readouts) and in the analysis worker (checker), from the same code.
 * Incremental: a dirty DAG (ARCHITECTURE.md §7.1) recompiles only what a ChangeSet touched; a dev
 * "verify" mode recompiles from scratch and diffs.
 *
 * The preview renderer consumes ONLY `FrameDrawList` (renderer-agnostic draw data), so the WebGL2
 * renderer and the engine are built in parallel against fixture-generated draw lists.
 */

import type { Sec, TempoMap } from '../time/types';
import type { Disposable, Mat2D, RGB, RGBAf, Unsubscribe, Vec2 } from '../util/types';
import type {
  AssetId,
  ChartDoc,
  ChartMeta,
  DocIndexes,
  EffectId,
  EffectShader,
  KeyId,
  LineId,
  LineKind,
  LineProp,
  NoteId,
  NoteKind,
  PackageSettings,
  TrackId,
  UiElement,
  VideoId,
} from '../model/types';
import type { ChangeSet } from '../ops/types';
import type { IrChart, IrExtraJson, IrLine, LowerOptions, LowerReport } from '../ir/types';

// ---------------------------------------------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------------------------------------------

/** Anything that holds a document and announces changes: the main-thread DocStore or a worker replica. */
export interface EngineSource {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  /** Committed and live (gesture) changes, in order. */
  subscribe(cb: (cs: ChangeSet) => void): Unsubscribe;
}

export interface EngineOptions {
  /** 'preview' compiles lazily per frame; 'check' compiles everything eagerly once per batch. */
  readonly purpose: 'preview' | 'check' | 'export';
  /**
   * Lowering inputs. `profile`, `speedMode` and `speedBakeTolPx` are ALWAYS derived from `doc.pkg` (re-read on
   * every pkg-dirty ChangeSet): the document is the single source of truth. `overrides` exist only for explicit
   * "what if" previews (e.g. the export dialog showing the Minimal profile).
   */
  readonly lower?: { readonly assetPath?: LowerOptions['assetPath']; readonly overrides?: Partial<LowerOptions> };
  /**
   * Dev builds: every N-th sync that did work runs the full verification (see Engine.verify) and throws an
   * AssertionError (core/util/assert) from that sync, listing the first 20 differences (0 = off).
   */
  readonly verifyEvery?: number;
}

/**
 * Pure respack numbers the engine needs to build note quads and hit effects (skin-and-assets.md S§2-4).
 * Produced by formats/respack (validated like Phira) or by the procedural skin.
 */
export interface SkinMetrics {
  /** Texture pixel sizes per slot (width, height). */
  readonly tex: Readonly<Record<NoteTexSlotName, readonly [w: number, h: number]>>;
  /** [tailPx, headPx] slices of hold.png / hold_mh.png. */
  readonly holdAtlas: readonly [number, number];
  readonly holdAtlasMH: readonly [number, number];
  /** hit_fx.png grid [cols, rows]. */
  readonly hitFx: readonly [number, number];
  readonly hitFxDuration: number;
  readonly hitFxScale: number;
  readonly hitFxRotate: boolean;
  readonly hitFxTinted: boolean;
  readonly hideParticles: boolean;
  readonly holdKeepHead: boolean;
  readonly holdRepeat: boolean;
  readonly holdCompact: boolean;
  /** Parsed respack colours (ARGB > 0xFFFFFF else RGB opaque). */
  readonly colorPerfect: RGBAf;
  readonly colorGood: RGBAf;
}

/** Texture slots in Phira's GL texture creation order (= bucket rank inside a note kind, rendering.md R§4.8). */
export type NoteTexSlotName =
  | 'click'
  | 'hold'
  | 'flick'
  | 'drag'
  | 'click_mh'
  | 'hold_mh'
  | 'flick_mh'
  | 'drag_mh'
  | 'holdBody'
  | 'holdBodyMh';

/** Numeric slot index = position in NOTE_TEX_SLOTS (also the texture rank used for bucket order). */
export const NOTE_TEX_SLOTS: readonly NoteTexSlotName[] = [
  'click',
  'hold',
  'flick',
  'drag',
  'click_mh',
  'hold_mh',
  'flick_mh',
  'drag_mh',
  'holdBody',
  'holdBodyMh',
];

/** Phira note kind order for buckets: Hold 0 < Drag 1 < Tap 2 < Flick 3. */
export const NOTE_KIND_ORDER = { hold: 0, drag: 1, tap: 2, flick: 3 } as const;

/** Letterboxed chart viewport (rendering.md R§1.2), all in device pixels of the target canvas. */
export interface Viewport {
  readonly winW: number;
  readonly winH: number;
  readonly vx: number;
  readonly vy: number;
  readonly W: number;
  readonly H: number;
  /** Effective aspect ratio W/H. */
  readonly ar: number;
}

/**
 * Preview-only view filters (never part of the document, never exported). Whenever any is active the
 * editor shows the banner "Preview differs from export".
 */
export interface PreviewFilters {
  readonly hiddenLines: ReadonlySet<LineId>;
  /** Non-empty: only these lines (and their notes) are drawn and sound. */
  readonly soloLines: ReadonlySet<LineId>;
  /** `${prop}:${layer}` per line; muted layers are left out of the channel sums. */
  readonly mutedLayers: ReadonlyMap<LineId, ReadonlySet<string>>;
  /**
   * Dim non-active lines and their notes by this factor (0..1; the dim STRENGTH: alpha x (1 - factor), clamped,
   * matching ViewFilterState.dimOthers, 0 = off; a NaN factor hides the dimmed bodies); null = off. Shown with a
   * "dimmed" badge.
   */
  readonly dim: { readonly activeLine: LineId; readonly factor: number } | null;
  /**
   * "Focus hitsounds" (VIEW-15): when non-empty, only these lines' emissions sound. Does not affect drawing and
   * does not count as "preview differs from export".
   */
  readonly focusHitsoundLines: ReadonlySet<LineId>;
}

/** Phira player settings emulated by the preview (labelled with Phira's names; never written to the chart). */
export interface PhiraPlayerSettings {
  /** Player aspect override (config.aspect_ratio); null = info.aspectRatio. */
  readonly aspectOverride: number | null;
  readonly noteScale: number;
  readonly doubleHint: boolean;
  readonly particles: boolean;
  /** "Aggressive" note culling (Phira default on), reproduced exactly incl. quirks. */
  readonly aggressiveCull: boolean;
  /** Phira ignores tintHitEffects on autoplay hits; true = reproduce that (default). */
  readonly strictAutoplayHitColor: boolean;
  /** MSAA is a Phira player setting (default off, sample_count 1); handled by the renderer. */
  readonly msaa: boolean;
  /** Phira `showAcc`: draw the accuracy HUD element (default false, as Phira). */
  readonly showAccuracy?: boolean;
  /**
   * Phira `apFcIndicator` (R§3.4, default on): the Perfect / Good / white `res.judge_line_color` ladder in
   * judged play. Off: the indicator does not apply and the line stays white (the editor and autoplay always
   * use the respack Perfect colour).
   */
  readonly apFcIndicator?: boolean;
}

export interface FrameOptions {
  readonly winW: number;
  readonly winH: number;
  readonly player: PhiraPlayerSettings;
  readonly skin: SkinMetrics;
  /** Global fade res.alpha (1 in the editor; intro/outro curves in test play). */
  readonly resAlpha: number;
  /** HUD slide progress p (1 in the editor). */
  readonly hudProgress: number;
  readonly showHud: boolean;
  readonly showEffects: boolean;
  readonly showVideos: boolean;
  /** Playback rate (hold tick interval = 0.15 / rate). */
  readonly rate: number;
  /** Track length in seconds (progress bar). */
  readonly trackLengthSec: number;
  readonly filters: PreviewFilters;
  /** Compute outlines of notes Phira hides by rule (editor overlay "why is my note not visible?"). */
  readonly ghosts: boolean;
  /** Judged test play (P0): judgement state per note. null = autoplay semantics (a note is judged at its time). */
  readonly judge: JudgeView | null;
  /**
   * Text width in UI units of `text` in `font` at `size` (the renderer's glyph measurer). The legacy HUD layout
   * (`useAttachUiFix: false`) scales score / name / level about their text centres, which needs real widths; without
   * it (or for a result that is not a finite width >= 0) widths are estimated from average advances.
   */
  readonly measureText?: (text: string, font: 'pgr' | 'ui', size: number) => number;
  /**
   * Phira player "background dim" emulation (preview only, never written to the chart); null / absent = the
   * chart's own info.yml backgroundDim (`doc.pkg`). Fills FrameDrawList.background.dim.
   */
  readonly backgroundDimOverride?: number | null;
}

export type JudgeGrade = 'perfect' | 'good' | 'bad' | 'miss';

/**
 * Read-only judgement state supplied by judged test play (src/features/preview, W2-02). Windows: Perfect ±80 ms,
 * Good ±160 ms, Bad ±220 ms (F§11.6). The engine uses it for note visibility, hold drawing and hit effects;
 * in judged play the audio scheduler gets no emission source and the play UI triggers hitsounds on hits.
 */
export interface JudgeView {
  /**
   * null = not judged yet. `holding`: a hold whose head was hit and that is still held. `releasedAt`: when a hold
   * was let go (bounds its tick bursts exactly; without it the engine reconstructs the release from the frames it
   * is shown).
   */
  status(
    note: NoteId,
  ): { readonly grade: JudgeGrade; readonly at: Sec; readonly holding: boolean; readonly releasedAt?: Sec } | null;
}

// ---------------------------------------------------------------------------------------------------------------
// Line states
// ---------------------------------------------------------------------------------------------------------------

/**
 * State of one line at time t. Objects are POOLED and overwritten on the next query: never keep
 * references across frames. World = Phira chart space (x in [-1,1], y up, isotropic).
 */
export interface LineStateRt {
  readonly line: LineId;
  /** Export index. */
  readonly index: number;
  readonly kind: LineKind;
  readonly uiMover: boolean;
  /**
   * Line alpha exactly as Phira computes it (DESIGN.md §6.2): per layer, keyframe values pre-multiplied by the
   * f32 factor fround(v * fround(1/255)), interpolated in f32, layers summed RIGHT-associatively in f32;
   * 0 when the line has no alpha track. Before res.alpha. `alpha < 0` (strictly) hides the line AND its notes;
   * this value, never channelValue('sum')/255, decides it (e.g. layers 68, -223, 155 give -2.98e-8: hidden).
   */
  alpha: number;
  /** Own translation in world units (moveX/675, moveY/450/ar). */
  ownX: number;
  ownY: number;
  /** Own rotation, world degrees CCW (= -Σ rotate). */
  ownRot: number;
  /** Composed with parents (F§10.1). */
  x: number;
  y: number;
  rot: number;
  /** Scale applied to the line drawable (factors and the x0.5 rule applied). */
  sx: number;
  sy: number;
  /** Raw scaleY value or null when absent (thickness quirk). */
  scaleYRaw: number | null;
  /** Colour from colour events (0..1) or null (default colour applies). */
  color: RGB | null;
  /** Current text for text lines (Phira string tween), else null. */
  text: string | null;
  /** Floor position H(t) (f32). */
  height: number;
  /** sin(incline at chart time 0) — Phira never advances incline. */
  inclineSin: number;
  /**
   * GIF loop phase in [0, 1) (GIF lines), else NaN: (gifProgress · totalMs) mod totalMs is Phira's frame position.
   * (Natural auto-play and event values may take the unwrapped progress past [0, 1]; the phase is what is drawn.)
   */
  gifProgress: number;
  /** Paint brush value (paint lines), else NaN. */
  paint: number;
  /**
   * Why Phira draws nothing for this line at t (null = something is drawn): opacity <= 0, zero scale, empty text
   * on a text line, UI-mover line, or a paint line whose canvas is cleared (brush <= 0; a paint line's opacity and
   * scale only affect new stamps, its canvas stays composited). Drives the editor's "show invisible lines" outlines.
   */
  drawsNothing: 'alpha' | 'scale' | 'emptyText' | 'uiMover' | 'paintCleared' | null;
  /** Translate(x, y) · Rotate(rot): local -> world. */
  m: Mat2D;
}

// ---------------------------------------------------------------------------------------------------------------
// Frame draw list (engine -> renderer)
// ---------------------------------------------------------------------------------------------------------------

/**
 * One line body. Colours: normal lines without colour events use the skin's Perfect colour with a x resAlpha (the
 * autoplay judge-line colour), then a x max(alpha · resAlpha, 0) for every colour (so default-coloured lines fade
 * with resAlpha², R§3.4); texture, GIF and text lines use a = max(alpha · resAlpha, 0) (replaces). Bodies Phira
 * draws nothing for (`drawsNothing` set, or a fully transparent colour) are not listed; paint lines are always listed
 * (their buffer stays visible). Preview filters apply: hidden lines and lines outside a non-empty solo set are not
 * listed; `dim` multiplies the alpha of non-active lines by 1 - factor.
 */
export type LineDraw =
  | {
      readonly kind: 'normal';
      readonly line: LineId;
      /** World matrix of the line drawable (includes scale). The quad is x in [-halfLen, halfLen], y in [-thickness/2, thickness/2]. */
      readonly m: Mat2D;
      readonly halfLen: number;
      readonly thickness: number;
      readonly color: RGBAf;
    }
  | {
      /**
       * Static texture (also a .gif without gifEvents: first frame only). Quad = unit square [-0.5, 0.5]² under
       * m = line matrix · Scale(sx · w, sy · h) (w, h = texture pixels from the asset metadata; sx, sy already carry
       * 2/1350); the image's top row goes to local +y. A texture without known pixel size is not listed. Same for 'gif'.
       */
      readonly kind: 'texture';
      readonly line: LineId;
      readonly m: Mat2D;
      readonly asset: AssetId;
      readonly color: RGBAf;
    }
  | {
      readonly kind: 'gif';
      readonly line: LineId;
      readonly m: Mat2D;
      readonly asset: AssetId;
      /** Position inside the GIF loop in ms: (progress * totalMs) mod totalMs. The renderer maps it to a frame. */
      readonly gifMs: number;
      readonly color: RGBAf;
    }
  | {
      /**
       * Text line: m = line matrix · Scale(sx, -sy) maps the text block's local space: the renderer lays the block out
       * in world units with y pointing down (upright text), em = 0.08 world units, anchored at the block centre (R§3.6).
       */
      readonly kind: 'text';
      readonly line: LineId;
      readonly m: Mat2D;
      readonly text: string;
      readonly color: RGBAf;
    }
  | {
      /** Paint line: the renderer composites the line's persistent buffer, fed by Engine.paintStamps. */
      readonly kind: 'paint';
      readonly line: LineId;
    };

/**
 * Note quads in Phira's draw order. Buffers are reused between frames (do not retain).
 * Quad corners are world coordinates in the order: (x0,y0) near-left, (x1,y1) near-right,
 * (x2,y2) far-right, (x3,y3) far-left, where "far" is the side the source rect's TOP maps to (flip_y).
 */
export interface NoteQuadBatch {
  count: number;
  /** 8 floats per quad. */
  readonly pos: Float32Array;
  /** 4 floats per quad: u0, v0 (top of source rect, maps to far edge), u1, v1. */
  readonly uv: Float32Array;
  /** 4 bytes per quad RGBA, explicitly CLAMPED to 0..255 (Uint8Array wraps modulo 256 otherwise). */
  readonly color: Uint8Array;
  /** Texture slot index (NOTE_TEX_SLOTS) per quad. */
  readonly slot: Uint8Array;
  /** Source note id per quad (overlay hit-testing). */
  readonly note: Int32Array;
  /**
   * Contiguous ranges, already in draw order (kindOrder asc, slot rank asc, push order). kindOrder is
   * NOTE_KIND_ORDER (0..3); in judged play `notes` may end with Bad-note ghost buckets of kindOrder 4
   * (`BAD_GHOST_KIND_ORDER`, core/engine/notes; R§5.5) whose quads carry note -1: renderers draw them after the
   * non-global effects (R§8 item 7), and anything that maps quads back to notes skips them (`isBadGhostBucket`).
   */
  readonly buckets: Array<{ kindOrder: number; slot: number; start: number; count: number }>;
}

export interface EffectDraw {
  readonly effect: EffectId;
  readonly shader: EffectShader;
  /** Uniform values evaluated at t (defaults from the shader source are applied by the renderer first). */
  readonly uniforms: ReadonlyArray<readonly [name: string, value: number | readonly number[]]>;
  /** Chart seconds (uniform `time`). */
  readonly time: Sec;
  readonly global: boolean;
}

export interface VideoDraw {
  readonly video: VideoId;
  readonly asset: AssetId;
  /**
   * Seconds since the video's start time (listed for every t >= start). Phira never loops videos: the renderer
   * draws nothing once localSec >= the decoded file's duration.
   */
  readonly localSec: number;
  readonly scale: 'cropCenter' | 'inside' | 'fit';
  /** Attached videos: line's OWN object transform in y-down camera space (Phira quirk); null = full viewport. */
  readonly m: Mat2D | null;
  readonly tint: RGBAf;
  readonly alpha: number;
  readonly dim: number;
}

/** Hit-effect sprite in UI space (y down), already including the alpha curve. */
export interface HitFxDraw {
  readonly x: number;
  readonly y: number;
  /** Screen rotation in degrees for a y-down canvas (= -world angle); 0 unless hitFxRotate. */
  readonly rotDeg: number;
  /** Half side in world units (hitFxScale * noteScale / 5). */
  readonly half: number;
  /** Atlas frame index (row-major). */
  readonly frame: number;
  readonly color: RGBAf;
}

/** Square particle in UI space (axis-aligned). */
export interface ParticleDraw {
  readonly x: number;
  readonly y: number;
  readonly half: number;
  readonly color: RGBAf;
}

export type HudElementId = UiElement | 'accuracy';

/**
 * One HUD element (rendering.md R§7). Positions/anchors in UI space (y down, x in [-1,1]).
 * The renderer measures text and applies `m` (attach transform incl. pivots) after layout.
 */
export interface HudElementDraw {
  readonly id: HudElementId;
  readonly visible: boolean;
  readonly text?: string;
  readonly font?: 'pgr' | 'ui';
  /** Text size factor: glyph PxScale = 0.04 * size * W px. */
  readonly size?: number;
  readonly pos: Vec2;
  readonly anchor: Vec2;
  readonly maxWidth?: number;
  /** Filled rects (pause bars, progress bar) in UI space before `m`. */
  readonly rects?: ReadonlyArray<readonly [x: number, y: number, w: number, h: number, color: RGBAf]>;
  readonly m: Mat2D;
  readonly color: RGBAf;
}

export interface HudDraw {
  readonly elements: readonly HudElementDraw[];
  /** Whole-HUD alpha (res.alpha). */
  readonly alpha: number;
}

export type GhostReason = 'cover' | 'negativeLineAlpha' | 'culled' | 'uiMoverLine' | 'beforeVisibleTime' | 'offscreen' | 'filtered';

/** Outline of a note that Phira does not draw at t, with the reason (editor overlay only). */
export interface GhostDraw {
  readonly note: NoteId;
  readonly reason: GhostReason;
  /** 8 floats, same corner order as NoteQuadBatch. */
  readonly quad: readonly number[];
}

export interface FrameDrawList {
  readonly t: Sec;
  readonly viewport: Viewport;
  readonly resAlpha: number;
  /**
   * `dim`: FrameOptions.backgroundDimOverride when given, else pkg.backgroundDim as stored; the renderer multiplies
   * it by resAlpha (R§1.3 step 4: viewport dim res.alpha · backgroundDim).
   */
  readonly background: { readonly illustration: AssetId | null; readonly dim: number };
  readonly videos: readonly VideoDraw[];
  /** Line bodies in (zOrder, index) order, attachUI lines excluded. */
  readonly lines: readonly LineDraw[];
  readonly notes: NoteQuadBatch;
  /** Non-global effects (after notes, before hit fx), in effect order. */
  readonly effects: readonly EffectDraw[];
  readonly hitFx: readonly HitFxDraw[];
  readonly particles: readonly ParticleDraw[];
  readonly hud: HudDraw | null;
  /** Global effects (over everything incl. HUD). */
  readonly globalEffects: readonly EffectDraw[];
  readonly ghosts: readonly GhostDraw[];
}

/** Writable view of a FrameDrawList used by the engine seams to fill pooled lists (arrays become mutable). */
export type FrameDrawListBuilder = {
  -readonly [K in keyof FrameDrawList]: FrameDrawList[K] extends readonly (infer T)[] ? T[] : FrameDrawList[K];
};

// ---------------------------------------------------------------------------------------------------------------
// Emissions (audio + hit effects), paint, stats
// ---------------------------------------------------------------------------------------------------------------

export type EmissionKind = 'hit' | 'holdStart' | 'holdTick' | 'holdEnd';

/** Autoplay events of non-fake notes on EVERY line (incl. UI-mover and negative-alpha lines), sorted by time. */
export interface Emission {
  readonly time: Sec;
  readonly kind: EmissionKind;
  readonly note: NoteId;
  readonly line: LineId;
  /** Sound to play (null for ticks/ends). 'asset' = custom hitsound file. */
  readonly sound: { readonly kind: 'skin'; readonly name: 'click' | 'drag' | 'flick' } | { readonly kind: 'asset'; readonly asset: AssetId } | null;
  /** Whether a hit-effect burst is emitted. */
  readonly fx: boolean;
  /** Kind of the note (the hitsound scheduler picks the per-type delay from it, also for custom sounds). */
  readonly noteKind: NoteKind;
}

/** Circle stamps of a paint line, simulated at fixed 60 Hz from the last clear up to t (Phira is frame-rate dependent). */
export interface PaintStampBatch {
  /** Chart time of the last clear (value <= 0) at or before t, or -Infinity. */
  readonly clearedAt: Sec;
  /** Stamps in time order: world x, y, radius (world units, includes line scale), colour. */
  readonly stamps: ReadonlyArray<{ readonly t: Sec; readonly x: number; readonly y: number; readonly rx: number; readonly ry: number; readonly color: RGBAf }>;
}

export interface HudValues {
  readonly score: number;
  readonly combo: number;
  /** Non-fake note count N. */
  readonly totalNotes: number;
  /** Judged fraction k / N (0..1); the progress BAR uses FrameOptions.trackLengthSec. */
  readonly progress: number;
}

/**
 * `irEvents` counts the events of every typed list of every lowered line (padding and speed-bake pieces included,
 * extra.json keyframes not included); `realNotes` is the sum of `numOfNotes`.
 */
export interface EngineStats {
  readonly lines: number;
  readonly notes: number;
  readonly realNotes: number;
  readonly keys: number;
  readonly irEvents: number;
  readonly lastSyncMs: number;
  readonly lastFrameMs: number;
}

// ---------------------------------------------------------------------------------------------------------------
// Engine API
// ---------------------------------------------------------------------------------------------------------------

export interface Engine extends Disposable {
  /** Bumps whenever compiled output may have changed (consumers cache on it). */
  readonly revision: number;
  /** Apply pending incremental recompilation now (otherwise done lazily by the first query). */
  sync(): void;
  readonly tempo: TempoMap;
  /**
   * Phira max_time (seconds) + 1 s: max endTime over EVERY note (holds or not; NoteSrc.nonHoldEnd included) and
   * over alpha/move/rotate events in EVERY written layer, scaleX/scaleY, and textEvents in `extended` on ANY line
   * (ignoredLists included), in Phira's scan order. It decides when floor positions freeze.
   */
  readonly maxTime: Sec;

  /** The lowered IR exactly as the exporter will write it (lowered lazily per line, cached). */
  ir(): IrChart;
  irExtra(): IrExtraJson | null;
  irLine(line: LineId): IrLine;
  lowerReport(): LowerReport;

  /**
   * States of all lines at t, in export order. Pooled; see LineStateRt. `ar`: effective aspect ratio (Viewport.ar of
   * the frame being drawn; positions in y depend on it); default: the chart aspect (pkg.aspectRatio).
   */
  lineStates(t: Sec, filters?: PreviewFilters, ar?: number): readonly LineStateRt[];
  lineState(line: LineId, t: Sec, filters?: PreviewFilters, ar?: number): LineStateRt;

  /**
   * Channel value in RPE units for DISPLAY (column headers, readouts): layer index or 'sum' over layers. NaN when
   * the document has no track for the channel (any layer for 'sum', the given layer otherwise; export padding does
   * not count; a GIF line with gifAutoLoop has a progress channel). Present channels read the factor-1 evaluation
   * of the lowered IR, i.e. exactly what the export carries. Rendering decisions (line alpha, alpha < 0 note hiding, positions) never use it: they come from
   * lineState, which applies Phira's factor-before-interpolation f32 path.
   */
  channelValue(line: LineId, prop: LineProp, t: Sec, layer?: number | 'sum'): number;
  /** Sample a channel at `out.length` evenly spaced times in [t0, t1] (timeline curves, Σ overlays). */
  sampleChannel(line: LineId, prop: LineProp, layer: number | 'sum', t0: Sec, t1: Sec, out: Float32Array): void;
  /** Floor position H(t) (Phira height units; 1 = 450 RPE-y px). */
  floorPosition(line: LineId, t: Sec): number;
  sampleFloor(line: LineId, t0: Sec, t1: Sec, out: Float32Array): void;
  /**
   * Effective scroll speed in RPE speed units = dH/dt of the floor curve the active integrator produces (what
   * actually moves the notes: e.g. a step for easingType 0 in rpe170 mode, k·f'(x)+b in the Legacy-170
   * sub-mode). The Scroll speed column draws this, not the event values.
   */
  sampleEffectiveSpeed(line: LineId, t0: Sec, t1: Sec, out: Float32Array): void;
  /** Key whose segment is active at t on a track (null before the first key). */
  segmentAt(track: TrackId, t: Sec): KeyId | null;

  /** Note timing from its (raw or normalised) triple via the tempo map, bit-exact with Phira. */
  noteSec(note: NoteId): Sec;
  noteEndSec(note: NoteId): Sec;
  /** Phira multi-highlight: another note anywhere has the bit-identical time (fakes and hold heads count). */
  isMultiHighlight(note: NoteId): boolean;
  /**
   * Seconds before its hit at which a note becomes visible, on Phira's 60 Hz frame grid within the 60 s before the
   * hit (a note on screen for the whole window reports 60); null if it is not visible in that window. Notes are
   * placed with the aspect of the given window (letterboxing, player override). `atLeast`: the caller only asks
   * "is the read time below atLeast?"; the scan may stop once that is decided, so a result above `atLeast` is a
   * lower bound (every answer to that question is unchanged; read-time rules pass their threshold).
   */
  noteAppearSec(
    note: NoteId,
    player: PhiraPlayerSettings,
    skin: SkinMetrics,
    winW: number,
    winH: number,
    atLeast?: number,
  ): Sec | null;
  /** Non-fake notes with time in [t0, t1], sorted by time then id (judged test play candidates). */
  notesInWindow(t0: Sec, t1: Sec): readonly NoteId[];
  /** Topmost note quad of `list` containing world point (x, y), in reverse draw order (preview click-to-select). */
  pickNote(list: FrameDrawList, x: number, y: number): NoteId | null;

  /**
   * Everything the preview draws at t, evaluated with the viewport's aspect. Without `out` a new list is allocated;
   * renderers pass the previous list as `out` to reuse its arrays, note batch and line-draw objects (nothing of a
   * list may be retained across frames).
   */
  frame(t: Sec, opts: FrameOptions, out?: FrameDrawList): FrameDrawList;
  /** Autoplay emissions with time in [t0, t1), for the hitsound scheduler (tick spacing uses rate). */
  emissions(t0: Sec, t1: Sec, rate: number, filters?: PreviewFilters): readonly Emission[];
  paintStamps(line: LineId, t: Sec, winW: number): PaintStampBatch;
  hud(t: Sec): HudValues;
  stats(): EngineStats;
  /**
   * Dev: recompile from scratch (own lowering options, seam stores, state pool and path -> asset lookup) and return
   * the differences, at most 50 (empty = consistent): settings, IR, extra IR, lowering report, tempo, max_time,
   * line states, floors, channels, controls, note seconds / multi-highlight, emissions, HUD and whole frames.
   */
  verify(): readonly string[];
}

export type CreateEngine = (src: EngineSource, opts: EngineOptions) => Engine;

/**
 * Seam between W1-05b (engine.ts) and W1-06 (src/core/engine/notes/). W1-06 implements it in
 * src/core/engine/notes/index.ts as `export const notesModule: EngineNotesModule`. Every Engine method about notes,
 * emissions, ghosts, hit effects, paint and the HUD delegates here; W1-05b owns FrameDrawList pooling and calls
 * the frame-filling methods in R§8 order. Note seconds exist ONLY here (W1-05b's queries delegate).
 */
export interface EngineNotesModule {
  /** Build per-line note runtimes (seconds from raw triples, Phira sort order, heights) from the IR line + floor curve. */
  compileLineNotes(ctx: EngineCompileContext, line: LineId): void;
  /** Forget a deleted line's runtime. */
  dropLine(ctx: EngineCompileContext, line: LineId): void;
  /** Tempo changed: every note second is stale. */
  onTempoChanged(ctx: EngineCompileContext): void;
  /** Rebuild the global multi-highlight map incrementally. */
  updateMultiHint(ctx: EngineCompileContext, changedLines: ReadonlySet<LineId>): void;
  rebuildEmissions(ctx: EngineCompileContext): void;
  noteSec(ctx: EngineCompileContext, note: NoteId): Sec;
  noteEndSec(ctx: EngineCompileContext, note: NoteId): Sec;
  isMultiHighlight(ctx: EngineCompileContext, note: NoteId): boolean;
  /** See Engine.noteAppearSec (same window and `atLeast` rules). */
  noteAppearSec(
    ctx: EngineCompileContext,
    note: NoteId,
    player: PhiraPlayerSettings,
    skin: SkinMetrics,
    winW: number,
    winH: number,
    atLeast?: number,
  ): Sec | null;
  notesInWindow(ctx: EngineCompileContext, t0: Sec, t1: Sec): readonly NoteId[];
  /**
   * Appends emissions with time in [t0, t1) to `out` (filters: solo, hidden, focusHitsoundLines). Hold ticks are
   * modelled on a 60 Hz frame grid: tick k at `time + 1/60 + k * 0.15 / rate` while `< endTime` (Phira emits the
   * first tick on the frame after the head is judged).
   */
  emissions(ctx: EngineCompileContext, t0: Sec, t1: Sec, rate: number, filters: PreviewFilters | undefined, out: Emission[]): void;
  /** Fills `out.notes` from scratch (may replace the batch with a larger one). */
  pushNoteQuads(ctx: EngineCompileContext, states: readonly LineStateRt[], t: Sec, opts: FrameOptions, out: FrameDrawListBuilder): void;
  /** Replaces `out.ghosts`. */
  ghosts(ctx: EngineCompileContext, states: readonly LineStateRt[], t: Sec, opts: FrameOptions, out: FrameDrawListBuilder): void;
  /** Replaces `out.hitFx` and `out.particles`. */
  hitFx(ctx: EngineCompileContext, t: Sec, opts: FrameOptions, out: FrameDrawListBuilder): void;
  /** `winW` is the viewport width W_px of the brush radius formula. */
  paintStamps(ctx: EngineCompileContext, line: LineId, t: Sec, winW: number): PaintStampBatch;
  hud(ctx: EngineCompileContext, states: readonly LineStateRt[], t: Sec, opts: FrameOptions, viewport: Viewport): HudDraw;
  hudValues(ctx: EngineCompileContext, t: Sec): HudValues;
}

/**
 * Seam between W1-05b (engine.ts) and W1-08 (src/core/engine/effects/). W1-08 implements it in
 * src/core/engine/effects/index.ts as `export const effectsModule: EngineEffectsModule` (extra.json keyframe rules:
 * clamp only for a non-default slice, no linear shortcut, extrapolation; active for t in [start, end)).
 */
export interface EngineEffectsModule {
  /** Fills `out.effects` (non-global) and `out.globalEffects`, in effect order. */
  effects(ctx: EngineCompileContext, t: Sec, opts: FrameOptions, out: FrameDrawListBuilder): void;
  /** Fills `out.videos` (timing, attach quirk). */
  videos(ctx: EngineCompileContext, t: Sec, opts: FrameOptions, out: FrameDrawListBuilder): void;
}

/** Read access the seam modules get to the motion half and the document-level settings (owned by W1-05b). */
export interface EngineCompileContext {
  readonly tempo: TempoMap;
  readonly maxTime: Sec;
  /** Package switches: holdPartialCover, noteUniformScale, useAttachUiFix (HUD layout), aspectRatio, lineLength, speedMode. */
  readonly pkg: PackageSettings;
  /** HUD texts. */
  readonly meta: Pick<ChartMeta, 'name' | 'level'>;
  irLine(line: LineId): IrLine;
  irExtra(): IrExtraJson | null;
  lineOrder(): readonly LineId[];
  floorAt(line: LineId, t: Sec): number;
  /**
   * Evaluates with the aspect ratio and preview filters of the frame being filled (else the chart aspect, no
   * filters). Uses a pool separate from `Engine.lineStates`; the returned object is valid until the next
   * `ctx.lineState` call.
   */
  lineState(line: LineId, t: Sec): LineStateRt;
  /** Evaluate a note control curve of a line at key h (null = absent/inert -> factor 1). */
  control(line: LineId, which: 'pos' | 'alpha' | 'size' | 'y', h: number): number | null;
  /** Per-engine scratch storage for the notes module (typed-array runtimes). */
  readonly notesStore: Map<LineId, unknown>;
  /** Per-engine scratch storage for the effects module. */
  readonly effectsStore: Map<string, unknown>;
  /**
   * Asset of a package path written by Lower (custom hitsounds, `/path` shaders without the '/', videos; one leading
   * '/' ignored); null when the package has no such file. The inverse of LowerOptions.assetPath (else stored paths).
   */
  assetIdForPath(path: string): AssetId | null;
}
